import React from 'react';
import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const DiscountBadgeContainer = styled.div<{ size?: 'small' | 'medium' | 'large' }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  ${props => {
    switch(props.size) {
      case 'small':
        return 'width: 40px; height: 40px; font-size: 0.7rem;';
      case 'large':  
        return 'width: 70px; height: 70px; font-size: 1.1rem;';
      default:
        return 'width: 55px; height: 55px; font-size: 0.9rem;';
    }
  }}
  
  background: linear-gradient(135deg, #ff4757 0%, #ff3838 100%);
  color: white;
  border-radius: 50%;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.4);
  border: 3px solid white;
  animation: ${pulse} 2s ease-in-out infinite;
  
  &:before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, #ff6b7a, #ff4757);
    border-radius: 50%;
    z-index: -1;
  }
`;

const PercentageText = styled.div`
  line-height: 1;
  text-align: center;
`;

const DiscountLabel = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  white-space: nowrap;
  margin-top: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  
  &:before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid #333;
  }
`;

const StyledRibbonBadge = styled.div<{ color?: string }>`
  position: absolute;
  top: 10px;
  left: -5px;
  background: ${props => props.color || '#ff4757'};
  color: white;
  padding: 4px 12px;
  font-size: 0.8rem;
  font-weight: bold;
  transform: rotate(-5deg);
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  z-index: 10;
  
  &:before {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-top: 5px solid rgba(0,0,0,0.2);
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 100%;
    right: 0;
    width: 0;
    height: 0;
    border-right: 5px solid transparent;
    border-top: 5px solid rgba(0,0,0,0.2);
  }
`;

const InlineDiscountText = styled.span<{ type?: 'percentage' | 'fixed' }>`
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
  
  &:before {
    content: ${props => props.type === 'fixed' ? '"-"' : '"%"'};
    font-size: 0.7rem;
  }
`;

interface DiscountBadgeProps {
  discountPercentage?: number;
  discountAmount?: number;
  discountType: 'percentage' | 'fixed';
  discountLabel?: string;
  variant?: 'circle' | 'ribbon' | 'inline';
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

const DiscountBadge: React.FC<DiscountBadgeProps> = ({
  discountPercentage = 0,
  discountAmount = 0,
  discountType,
  discountLabel,
  variant = 'circle',
  size = 'medium',
  showLabel = false
}) => {
  const getDiscountText = () => {
    if (discountType === 'percentage') {
      return `-${discountPercentage}%`;
    } else {
      return `-${discountAmount}kr`;
    }
  };

  if (variant === 'inline') {
    return (
      <InlineDiscountText type={discountType}>
        {getDiscountText()}
      </InlineDiscountText>
    );
  }

  if (variant === 'ribbon') {
    return (
      <StyledRibbonBadge>
        {discountLabel || getDiscountText()}
      </StyledRibbonBadge>
    );
  }

  return (
    <DiscountBadgeContainer size={size}>
      <PercentageText>
        {getDiscountText()}
      </PercentageText>
      {showLabel && discountLabel && (
        <DiscountLabel>
          {discountLabel}
        </DiscountLabel>
      )}
    </DiscountBadgeContainer>
  );
};

export default DiscountBadge;